import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LogOut, Package } from "lucide-react";
import { API, money } from "@/lib/site";
import { setToken } from "@/lib/admin";

export default function Account() {
  const nav = useNavigate();
  const location = useLocation();
  // three states: null = checking, false = not signed in, object = user
  const [user, setUser] = useState(location.state?.user || null);
  const [checked, setChecked] = useState(!!location.state?.user);
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    document.title = "My Account | Regal Tuning";
    if (location.state?.user) return; // came straight from the sign-in callback
    (async () => {
      try {
        const { data } = await axios.get(`${API}/account/me`, { withCredentials: true });
        setUser(data);
      } catch {
        nav("/sign-in", { replace: true });
        return;
      } finally {
        setChecked(true);
      }
    })();
  }, [location, nav]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/account/orders`, { withCredentials: true })
      .then(({ data }) => setOrders(data.orders || []))
      .catch(() => setOrders([]));
  }, [user]);

  const signOut = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { /* cookie may already be gone */ }
    setToken(null);
    nav("/", { replace: true });
  };

  if (!checked) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center" data-testid="account-loading">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">Loading your account…</p>
      </main>
    );
  }
  if (!user) return null;

  return (
    <main className="bg-surface" data-testid="account-page">
      <div className="mx-auto max-w-3xl px-5 py-14 sm:py-20">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} alt="" className="h-14 w-14 rounded-full border border-line object-cover" data-testid="account-avatar" />
            ) : (
              <div className="grid h-14 w-14 place-items-center rounded-full border border-line bg-white font-display text-lg font-bold text-regal-amber" data-testid="account-avatar-fallback">
                {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-regal-amber">My account</p>
              <h1 className="mt-1 font-display text-2xl font-bold uppercase tracking-[0.04em] text-ink" data-testid="account-name">{user.name || "Customer"}</h1>
              <p className="text-sm text-neutral-500" data-testid="account-email">{user.email}</p>
            </div>
          </div>
          <button onClick={signOut} data-testid="sign-out-button"
            className="flex items-center gap-2 border border-line bg-white px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500 transition-colors hover:border-regal-amber hover:text-regal-amber">
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <section className="mt-10 border border-line bg-white" data-testid="account-orders">
          <div className="flex items-center gap-2 border-b border-line px-6 py-4">
            <Package size={16} className="text-regal-amber" />
            <h2 className="font-display text-sm font-bold uppercase tracking-[0.16em] text-ink">Your orders</h2>
          </div>
          {orders === null ? (
            <p className="px-6 py-8 text-sm text-neutral-500">Loading orders…</p>
          ) : orders.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <p className="text-sm text-neutral-500" data-testid="orders-empty">No orders yet on this account.</p>
              <Link to="/tuning-tools" data-testid="orders-browse-link"
                className="mt-5 inline-block border border-regal-amber px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.16em] text-regal-amber transition-colors hover:bg-regal-amber hover:text-void">
                Browse tuning tools
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-line">
              {orders.map((o) => (
                <li key={o.reference} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4" data-testid={`order-row-${o.reference}`}>
                  <div>
                    <p className="font-mono text-sm text-ink">{o.reference}</p>
                    <p className="mt-0.5 text-xs text-neutral-500">
                      {(o.created_at || "").slice(0, 10)} · {o.items?.map((i) => `${i.qty}× ${i.name}`).join(", ")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-regal-amber">{money(o.total_inc_vat, o.items?.[0]?.currency || "GBP")}</p>
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-400" data-testid={`order-status-${o.reference}`}>{o.status}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}
